import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BRAND_SUPPLY_LABELS,
  BRAND_SUPPLY_MODES,
  DASHBOARD_RANGE_LABELS,
  FRANCHISE_PRICE_MODE_LABELS,
  FRANCHISE_PRICE_MODES,
  PAYMENT_METHOD_LABELS,
  type BranchDTO,
  type DashboardRange,
  type FranchiseAccountDTO,
  type FranchiseInvoiceDTO,
  type FranchiseOrderDTO,
  type Paged,
  type FranchiseOverviewDTO,
  type FranchiseRoyaltyDTO,
  type FranchiseSettingsDTO,
  type FranchiseStatsDTO,
  type PaymentMethod,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Kpi,
  PageHeader,
  Select,
  Skeleton,
  useToast,
} from '@bifeai/ui';
import { ChartColumn, FileText, HandCoins, Network, Plus, ReceiptText, Store, Truck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { centsToPesosInput, formatQty, fromInternalQty, parseDecimal, pesosToCents, toInternalQty } from '../../lib/units';
import { SwitchRow } from '../../components/SwitchRow';
import { FranchiseStatusCard } from '../settings/FranchiseStatusCard';
import {
  AccountTable,
  balanceText,
  fiscalLabel,
  FiscalStatus,
  InvoicesTable,
  LinesEditor,
  newLine,
  OrdersTable,
  Pager,
  RangeFilter,
  useListRange,
  parseLines,
  periodLabel,
  PrintInvoiceButton,
  useStockProducts,
  type DraftLine,
} from './common';

type Tab = 'summary' | 'rules' | 'orders' | 'account' | 'royalties' | 'invoices';
const TABS: { key: Tab; label: string }[] = [
  { key: 'summary', label: 'Resumen' },
  { key: 'rules', label: 'Reglas' },
  { key: 'orders', label: 'Pedidos y envíos' },
  { key: 'account', label: 'Cuenta corriente' },
  { key: 'royalties', label: 'Regalías' },
  { key: 'invoices', label: 'Facturas' },
];

/** ¿La cuenta tiene el adicional de facturación electrónica (ARCA)? */
function useArca(): boolean {
  const { me } = useSession();
  return me.tenant.modules.includes('ARCA');
}

/** Aviso cuando la factura no pudo salir (el movimiento ya quedó cargado). */
function warnInvoice(toast: ReturnType<typeof useToast>, problem: string | null | undefined, what: string) {
  if (problem)
    toast.info(`${what}: la factura quedó para después`, `${problem} Cuando esté resuelto, emitila desde Cuenta corriente con “Facturar”.`);
}

/**
 * Franquicias (casa central): todo lo de la red de franquicias, separado de los locales propios.
 * Números, reglas de cada franquicia, mercadería que les manda la marca, cuenta corriente y regalías.
 */
export function FranchisesPage() {
  const [tab, setTab] = useState<Tab>(() => (new URLSearchParams(location.search).get('tab') as Tab) || 'summary');
  const list = useQuery({ queryKey: ['franchises'], queryFn: () => api.get<FranchiseOverviewDTO[]>('/franchises') });
  return (
    <>
      <PageHeader
        title="Franquicias"
        subtitle="Cada franquicia es otra empresa: cobra, factura y tiene su plata. Acá ves sus números y manejás lo que la marca define."
      />
      <div className="bf-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      {list.isError ? (
        <Callout tone="danger">
          No se pudieron cargar las franquicias: {errorMessage(list.error)}{' '}
          <Button size="sm" onClick={() => void list.refetch()} loading={list.isFetching}>
            Reintentar
          </Button>
        </Callout>
      ) : !list.data ? (
        <Skeleton style={{ height: 260 }} />
      ) : !list.data.length ? (
        <EmptyState
          icon={<Network />}
          title="Todavía no hay franquicias"
          text="En Sucursales, creá una sucursal nueva (o editá una) y prendé “Es una franquicia”."
        />
      ) : tab === 'summary' ? (
        <Summary list={list.data} />
      ) : tab === 'rules' ? (
        <Rules list={list.data} />
      ) : tab === 'orders' ? (
        <Orders list={list.data} />
      ) : tab === 'account' ? (
        <Accounts list={list.data} />
      ) : tab === 'royalties' ? (
        <Royalties />
      ) : (
        <Invoices list={list.data} />
      )}
    </>
  );
}

// ---------------------------------------------------------------- resumen
const RANGES: DashboardRange[] = ['today', '7d', '30d', '90d', '6m', '1y'];

function Summary({ list }: { list: FranchiseOverviewDTO[] }) {
  const [range, setRange] = useState<DashboardRange>('30d');
  const stats = useQuery({ queryKey: ['franchises', 'stats', range], queryFn: () => api.get<FranchiseStatsDTO>(`/franchises/stats?range=${range}`) });
  const s = stats.data;
  const max = Math.max(...(s?.monthly.map((m) => m.salesCents) ?? [0]), 1);
  return (
    <div className="bf-form" style={{ gap: 16 }}>
      <Card>
        <CardHeader
          title="Ventas de la red de franquicias"
          subtitle="Lo que venden todas las franquicias juntas. Es plata de cada franquiciado, no de la marca: de acá salen las regalías."
          actions={
            <Select aria-label="Período" value={range} onChange={(e) => setRange(e.target.value as DashboardRange)}>
              {RANGES.map((r) => (
                <option key={r} value={r}>
                  {DASHBOARD_RANGE_LABELS[r]}
                </option>
              ))}
            </Select>
          }
        />
        <CardBody>
          {!s ? (
            <Skeleton style={{ height: 110 }} />
          ) : (
            <div className="bf-kpi-grid">
              <Kpi label="Ventas" value={formatMoney(s.totals.salesCents)} icon={<ChartColumn />} trend={s.monthly.map((m) => m.salesCents)} />
              <Kpi label="Tickets" value={s.totals.count} delta={`Ticket promedio ${formatMoney(s.totals.avgTicketCents)}`} deltaMuted tone="green" />
              <Kpi label="Facturado en ARCA" value={formatMoney(s.totals.invoicedCents)} icon={<ReceiptText />} tone="amber" />
              <Kpi label="Regalías del período" value={formatMoney(s.totals.royaltyCents)} icon={<HandCoins />} tone="red" />
            </div>
          )}
        </CardBody>
      </Card>

      {s && s.totals.byMethod.length > 0 && (
        <Card>
          <CardHeader title="¿Cómo cobran?" subtitle="Medios de pago de todas las franquicias en el período." />
          <CardBody>
            <div className="bf-channel-list">
              {s.totals.byMethod.map((m) => (
                <div key={m.method} className="bf-mp-branch">
                  <span>{PAYMENT_METHOD_LABELS[m.method as PaymentMethod] ?? m.method}</span>
                  <span>
                    <strong>{formatMoney(m.amountCents)}</strong>{' '}
                    <span className="bf-cell-sub">
                      {s.totals.salesCents ? Math.round((m.amountCents / s.totals.salesCents) * 100) : 0} % · {m.count} cobros
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Cada franquicia"
          subtitle="Ventas, tickets, facturación y regalías del período; saldo de su cuenta corriente con la marca."
        />
        <CardBody flush>
          <div className="bf-table-wrap">
            <table className="bf-table">
              <thead>
                <tr>
                  <th>Franquicia</th>
                  <th className="bf-num">Ventas</th>
                  <th className="bf-num">Tickets</th>
                  <th className="bf-num">Ticket prom.</th>
                  <th className="bf-num">Facturado</th>
                  <th className="bf-num">Regalías</th>
                  <th className="bf-num">Cuenta corriente</th>
                </tr>
              </thead>
              <tbody>
                {list.map((f) => {
                  const st = s?.franchises.find((x) => x.branchId === f.branchId);
                  return (
                    <tr key={f.branchId}>
                      <td>
                        <span className="bf-cell-main">{f.name}</span>
                        {f.pendingOrders > 0 && (
                          <div>
                            <Badge tone="warning">
                              {f.pendingOrders} pedido{f.pendingOrders === 1 ? '' : 's'} sin enviar
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="bf-num">{st ? formatMoney(st.salesCents) : '—'}</td>
                      <td className="bf-num">{st?.count ?? '—'}</td>
                      <td className="bf-num">{st ? formatMoney(st.avgTicketCents) : '—'}</td>
                      <td className="bf-num">{st ? formatMoney(st.invoicedCents) : '—'}</td>
                      <td className="bf-num">
                        {st ? formatMoney(st.royaltyCents) : '—'}
                        <div className="bf-cell-sub">{f.settings.royaltyPct} %</div>
                      </td>
                      <td className="bf-num">{balanceText(f.balanceCents)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {s && (
        <Card>
          <CardHeader title="Evolución mes a mes" subtitle="Ventas de toda la red de franquicias en los últimos 12 meses." />
          <CardBody>
            <div className="bf-month-bars" role="img" aria-label="Ventas por mes">
              {s.monthly.map((m) => (
                <div key={m.period} className="bf-month-bar" title={`${periodLabel(m.period)}: ${formatMoney(m.salesCents)}`}>
                  <span style={{ height: `${Math.max((m.salesCents / max) * 100, m.salesCents ? 4 : 0)}%` }} />
                  <small>{periodLabel(m.period).slice(0, 3)}</small>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      <FranchiseStatusCard />
    </div>
  );
}

// ---------------------------------------------------------------- reglas
function Rules({ list }: { list: FranchiseOverviewDTO[] }) {
  return (
    <div className="bf-form" style={{ gap: 16 }}>
      <Callout tone="info">
        Las reglas las define solo la casa central y rigen al momento. Si una franquicia deja de poder poner precios (o se achica el margen), sus
        precios que ya no corresponden se borran y vuelve a regir el de la marca.
      </Callout>
      {list.map((f) => (
        <RulesCard key={f.branchId} franchise={f} />
      ))}
    </div>
  );
}

function RulesCard({ franchise }: { franchise: FranchiseOverviewDTO }) {
  const arca = useArca();
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState<FranchiseSettingsDTO>(franchise.settings);
  const [pct, setPct] = useState(String(franchise.settings.royaltyPct));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setV(franchise.settings);
    setPct(String(franchise.settings.royaltyPct));
  }, [franchise.settings]);
  const dirty = JSON.stringify(v) !== JSON.stringify(franchise.settings) || pct !== String(franchise.settings.royaltyPct);

  async function save() {
    const royaltyPct = parseDecimal(pct);
    if (royaltyPct === null || royaltyPct < 0 || royaltyPct > 50) return setError('Las regalías van de 0 a 50 %.');
    setBusy(true);
    setError(null);
    try {
      await api.put(`/franchises/${franchise.branchId}/settings`, { ...v, royaltyPct });
      await qc.invalidateQueries({ queryKey: ['franchises'] });
      toast.success('Reglas guardadas', franchise.name);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader title={franchise.name} subtitle={franchise.address ?? undefined} />
      <CardBody>
        <div className="bf-form">
          <div className="bf-form-row">
            <Field label="Precios" hint="Los precios de la marca son los de Precios (generales).">
              <Select value={v.priceMode} onChange={(e) => setV({ ...v, priceMode: e.target.value as FranchiseSettingsDTO['priceMode'] })}>
                {FRANCHISE_PRICE_MODES.map((m) => (
                  <option key={m} value={m}>
                    {FRANCHISE_PRICE_MODE_LABELS[m]}
                  </option>
                ))}
              </Select>
            </Field>
            {v.priceMode === 'RANGE' && (
              <Field label="Margen permitido (%)" hint="Arriba o abajo del precio de la marca.">
                <Input
                  inputMode="numeric"
                  value={String(v.priceRangePct)}
                  onChange={(e) => setV({ ...v, priceRangePct: Math.min(Math.max(Number(e.target.value.replace(/\D/g, '')) || 1, 1), 50) })}
                />
              </Field>
            )}
          </div>
          <Field label="Mercadería de la marca" hint="Vendida: cada envío se le carga a su cuenta corriente al precio que pongas.">
            <Select value={v.brandSupply} onChange={(e) => setV({ ...v, brandSupply: e.target.value as FranchiseSettingsDTO['brandSupply'] })}>
              {BRAND_SUPPLY_MODES.map((m) => (
                <option key={m} value={m}>
                  {BRAND_SUPPLY_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
          <SwitchRow
            label="Le compra a los proveedores homologados por la marca"
            hint="Los homologados se marcan en Proveedores. Ve solo esos, nunca el resto de tus proveedores."
            checked={v.approvedSuppliers}
            onChange={(c) => setV({ ...v, approvedSuppliers: c })}
          />
          <SwitchRow
            label="Carga sus propios proveedores"
            hint="Los proveedores que cargue los ve solo ella."
            checked={v.ownSuppliers}
            onChange={(c) => setV({ ...v, ownSuppliers: c })}
          />
          <Field label="Regalías (% de las ventas del mes)" hint="Si la marca es responsable inscripta, se les suma el 21 % de IVA.">
            <Input inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value)} style={{ maxWidth: 160 }} />
          </Field>
          {arca && (
            <SwitchRow
              label="Facturar automáticamente"
              hint="La marca le hace la factura electrónica (ARCA) con su CUIT al de la franquicia: por cada envío vendido y por las regalías del mes. Si falta algún dato, queda para emitirla después."
              checked={v.autoInvoice}
              onChange={(c) => setV({ ...v, autoInvoice: c })}
            />
          )}
          {error && <Callout tone="danger">{error}</Callout>}
          <div>
            <Button variant="primary" loading={busy} disabled={!dirty} onClick={() => void save()}>
              Guardar reglas
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------- pedidos y envíos
function Orders({ list }: { list: FranchiseOverviewDTO[] }) {
  const [status, setStatus] = useState('REQUESTED');
  const [open, setOpen] = useState<FranchiseOrderDTO | null>(null);
  const [creating, setCreating] = useState(false);
  const range = useListRange();
  const orders = useQuery({
    queryKey: ['franchises', 'orders', status, ...range.key],
    queryFn: () => api.get<Paged<FranchiseOrderDTO>>(`/franchises/orders?${status ? `status=${status}&` : ''}${range.qs}`),
    placeholderData: keepPreviousData,
  });
  return (
    <Card>
      <CardHeader
        title="Pedidos y envíos de mercadería"
        subtitle="La franquicia pide, la marca envía desde uno de sus locales. Sale del stock de ese local y entra en el de la franquicia."
        actions={
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <Select
              aria-label="Estado"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                range.setPage(1);
              }}
            >
              <option value="REQUESTED">Pedidos sin enviar</option>
              <option value="SENT">Enviados</option>
              <option value="CANCELED">Cancelados</option>
              <option value="">Todos</option>
            </Select>
            <Button icon={<Plus />} onClick={() => setCreating(true)}>
              Nuevo envío
            </Button>
          </span>
        }
      />
      <CardBody flush>
        <div style={{ padding: '0 16px 12px' }}>
          <RangeFilter range={range} />
        </div>
        {!orders.data ? (
          <Skeleton style={{ height: 120, margin: 16 }} />
        ) : (
          <>
            <OrdersTable orders={orders.data.items} onOpen={setOpen} showBranch />
            <Pager range={range} total={orders.data.total} />
          </>
        )}
      </CardBody>
      {open && <SendDialog order={open} onClose={() => setOpen(null)} />}
      {creating && <ShipmentDialog list={list} onClose={() => setCreating(false)} />}
    </Card>
  );
}

function useOwnBranches() {
  const { me } = useSession();
  return me.branches.filter((b: BranchDTO) => !b.franchise);
}

function SendDialog({ order, onClose }: { order: FranchiseOrderDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const own = useOwnBranches();
  const [origin, setOrigin] = useState(own[0]?.id ?? '');
  const charge = order.mode === 'ORDER';
  const [rows, setRows] = useState(() =>
    order.items.map((i) => ({
      id: i.id,
      qty: fromInternalQty(i.unitType, i.qtySent ?? i.qtyRequested),
      price: centsToPesosInput(i.unitPriceCents ?? i.suggestedPriceCents ?? 0),
    })),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editable = order.status === 'REQUESTED';
  const total = useMemo(
    () =>
      order.items.reduce((acc, i, n) => {
        const q = toInternalQty(i.unitType, rows[n]!.qty) ?? 0;
        const p = pesosToCents(rows[n]!.price) ?? 0;
        return acc + Math.round((q * p) / (i.unitType === 'WEIGHT' ? 1000 : 1));
      }, 0),
    [rows, order.items],
  );

  async function run(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setError(null);
    try {
      const r = (await fn()) as Partial<FranchiseOrderDTO> | undefined;
      await qc.invalidateQueries({ queryKey: ['franchises'] });
      toast.success(ok, `Pedido #${order.number} · ${order.branchName}`);
      warnInvoice(toast, r?.invoiceProblem, `Pedido #${order.number}`);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function send() {
    const items = order.items.map((i, n) => ({
      itemId: i.id,
      qty: toInternalQty(i.unitType, rows[n]!.qty || '0') ?? -1,
      unitPriceCents: charge ? (pesosToCents(rows[n]!.price) ?? -1) : 0,
    }));
    if (items.some((i) => i.qty < 0 || i.unitPriceCents < 0)) return setError('Revisá las cantidades y los precios.');
    void run(() => api.post<FranchiseOrderDTO>(`/franchises/orders/${order.id}/send`, { originBranchId: origin, items }), 'Envío realizado');
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Pedido #${order.number} · ${order.branchName}`}
      description={
        charge
          ? 'Venta a cuenta corriente: al enviar, el total se le carga a la franquicia (y se factura, si está prendido). Los precios son finales, con IVA. El sugerido es el costo promedio de tu stock.'
          : 'Envío sin cargo: la mercadería entra a la franquicia a su costo y no suma deuda.'
      }
    >
      <div className="bf-form">
        {order.notes && <Callout tone="info">“{order.notes}”</Callout>}
        {editable && (
          <Field label="Sale desde">
            <Select value={origin} onChange={(e) => setOrigin(e.target.value)}>
              {own.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div className="bf-table-wrap">
          <table className="bf-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th className="bf-num">Pidió</th>
                <th>Envío</th>
                {charge && <th>Precio</th>}
              </tr>
            </thead>
            <tbody>
              {order.items.map((i, n) => (
                <tr key={i.id}>
                  <td className="bf-cell-main">{i.productName}</td>
                  <td className="bf-num">{formatQty(i.unitType, i.qtyRequested)}</td>
                  <td>
                    <Input
                      aria-label="Cantidad a enviar"
                      inputMode="decimal"
                      disabled={!editable}
                      value={rows[n]!.qty}
                      onChange={(e) => setRows(rows.map((r, k) => (k === n ? { ...r, qty: e.target.value } : r)))}
                      style={{ maxWidth: 110 }}
                    />
                  </td>
                  {charge && (
                    <td>
                      <Input
                        aria-label="Precio"
                        inputMode="decimal"
                        disabled={!editable}
                        value={rows[n]!.price}
                        onChange={(e) => setRows(rows.map((r, k) => (k === n ? { ...r, price: e.target.value } : r)))}
                        style={{ maxWidth: 130 }}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {charge && (
          <p style={{ margin: 0 }}>
            Total a cargar en su cuenta: <strong>{formatMoney(order.status === 'SENT' ? order.totalCents : total)}</strong>
          </p>
        )}
        {order.fiscal && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{fiscalLabel(order.fiscal)}</span>
            <FiscalStatus f={order.fiscal} />
            <PrintInvoiceButton base="/franchises/invoices" id={order.fiscal.id} label="Imprimir" />
          </div>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-actions">
          {editable && (
            <Button
              variant="ghost"
              loading={busy}
              onClick={() => void run(() => api.post(`/franchises/orders/${order.id}/cancel`), 'Pedido cancelado')}
            >
              Cancelar pedido
            </Button>
          )}
          <Button onClick={onClose}>Cerrar</Button>
          {editable && (
            <Button variant="primary" icon={<Truck />} loading={busy} disabled={!origin} onClick={send}>
              Enviar
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

function ShipmentDialog({ list, onClose }: { list: FranchiseOverviewDTO[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const own = useOwnBranches();
  const products = useStockProducts();
  const allowed = list.filter((f) => f.settings.brandSupply !== 'NONE');
  const [branchId, setBranchId] = useState(allowed[0]?.branchId ?? '');
  const [origin, setOrigin] = useState(own[0]?.id ?? '');
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const charge = allowed.find((f) => f.branchId === branchId)?.settings.brandSupply === 'ORDER';

  async function send() {
    const items = parseLines(lines, products.data, charge);
    if (!items) return setError('Completá cada producto con su cantidad' + (charge ? ' y precio.' : '.'));
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<FranchiseOrderDTO>('/franchises/shipments', { branchId, originBranchId: origin, items });
      await qc.invalidateQueries({ queryKey: ['franchises'] });
      toast.success('Envío realizado');
      warnInvoice(toast, r.invoiceProblem, `Envío #${r.number}`);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Nuevo envío a una franquicia">
      <div className="bf-form">
        {!allowed.length ? (
          <Callout tone="warning">Ninguna franquicia recibe mercadería de la marca. Cambialo en Reglas.</Callout>
        ) : (
          <>
            <div className="bf-form-row">
              <Field label="Franquicia">
                <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  {allowed.map((f) => (
                    <option key={f.branchId} value={f.branchId}>
                      {f.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Sale desde">
                <Select value={origin} onChange={(e) => setOrigin(e.target.value)}>
                  {own.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <p className="bf-hint" style={{ margin: 0 }}>
              {charge ? 'Venta a cuenta corriente: poné el precio final (con IVA) por kilo o por unidad.' : 'Envío sin cargo.'}
            </p>
            <LinesEditor lines={lines} onChange={setLines} withPrice={charge} />
          </>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" icon={<Truck />} loading={busy} disabled={!allowed.length} onClick={() => void send()}>
            Enviar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- cuenta corriente
function Accounts({ list }: { list: FranchiseOverviewDTO[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [branchId, setBranchId] = useState(list[0]!.branchId);
  const [adding, setAdding] = useState<'PAYMENT' | 'ADJUSTMENT' | null>(null);
  const range = useListRange();
  const acc = useQuery({
    queryKey: ['franchises', 'account', branchId, ...range.key],
    queryFn: () => api.get<FranchiseAccountDTO>(`/franchises/${branchId}/account?${range.qs}`),
    placeholderData: keepPreviousData,
  });
  return (
    <Card>
      <CardHeader
        title="Cuenta corriente con la marca"
        subtitle="Mercadería vendida y regalías suman deuda; los pagos la bajan. Un error se corrige con un ajuste."
        actions={
          <Select
            aria-label="Franquicia"
            value={branchId}
            onChange={(e) => {
              setBranchId(e.target.value);
              range.setPage(1);
            }}
          >
            {list.map((f) => (
              <option key={f.branchId} value={f.branchId}>
                {f.name}
              </option>
            ))}
          </Select>
        }
      />
      <CardBody>
        {!acc.data ? (
          <Skeleton style={{ height: 120 }} />
        ) : (
          <div className="bf-form">
            <div className="bf-mp-branch">
              <span>
                <Store size={16} aria-hidden /> <strong>{acc.data.branchName}</strong>
              </span>
              <strong style={{ fontSize: '1.2rem' }}>{balanceText(acc.data.balanceCents)}</strong>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', gap: 8 }}>
                <Button variant="primary" onClick={() => setAdding('PAYMENT')}>
                  Registrar pago
                </Button>
                <Button onClick={() => setAdding('ADJUSTMENT')}>Ajuste</Button>
              </span>
              <RangeFilter range={range} />
            </div>
            <AccountTable
              account={acc.data}
              base="/franchises/invoices"
              onInvoice={async (m) => {
                try {
                  await api.post<FranchiseAccountDTO>(`/franchises/${branchId}/account/${m.id}/invoice`);
                  await qc.invalidateQueries({ queryKey: ['franchises'] });
                  toast.success('Factura emitida', m.description);
                } catch (err) {
                  toast.error('No se pudo facturar', errorMessage(err));
                }
              }}
            />
            <Pager range={range} total={acc.data.total ?? 0} />
          </div>
        )}
      </CardBody>
      {adding && <MovementDialog branchId={branchId} kind={adding} onClose={() => setAdding(null)} />}
    </Card>
  );
}

function MovementDialog({ branchId, kind, onClose }: { branchId: string; kind: 'PAYMENT' | 'ADJUSTMENT'; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [sign, setSign] = useState<'+' | '-'>('-');
  const [description, setDescription] = useState(kind === 'PAYMENT' ? 'Transferencia' : '');
  const [invoiceId, setInvoiceId] = useState('');
  const arca = useArca();
  const invoices = useQuery({
    queryKey: ['franchises', 'invoices', branchId],
    queryFn: () => api.get<FranchiseInvoiceDTO[]>(`/franchises/invoices?branchId=${branchId}`),
    enabled: kind === 'ADJUSTMENT' && arca,
  });
  const bills = invoices.data?.filter((i) => i.kind === 'INVOICE' && i.status !== 'REJECTED') ?? [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [descError, setDescError] = useState<string | null>(null);
  async function save() {
    const cents = pesosToCents(amount);
    if (!cents || cents <= 0) return setError('Poné el importe.');
    if (description.trim().length < 2) {
      setDescError('Escribí un detalle (por ejemplo: flete, bonificación, mercadería en mal estado).');
      return;
    }
    setDescError(null);
    setBusy(true);
    setError(null);
    try {
      await api.post(`/franchises/${branchId}/account`, {
        kind,
        amountCents: kind === 'PAYMENT' ? cents : sign === '+' ? cents : -cents,
        description,
        invoiceId: kind === 'ADJUSTMENT' && invoiceId ? invoiceId : null,
      });
      await qc.invalidateQueries({ queryKey: ['franchises'] });
      toast.success(kind === 'PAYMENT' ? 'Pago registrado' : 'Ajuste registrado');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={kind === 'PAYMENT' ? 'Registrar pago de la franquicia' : 'Ajuste de la cuenta'}>
      <div className="bf-form">
        {kind === 'ADJUSTMENT' && (
          <Field label="Tipo de ajuste">
            <Select value={sign} onChange={(e) => setSign(e.target.value as '+' | '-')}>
              <option value="-">Le resta deuda (nota de crédito, bonificación)</option>
              <option value="+">Le suma deuda (cargo que faltó)</option>
            </Select>
          </Field>
        )}
        <Field label="Importe ($)">
          <Input inputMode="decimal" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Detalle" error={descError ?? undefined}>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        {kind === 'ADJUSTMENT' && bills.length > 0 && (
          <Field
            label="Comprobante en ARCA"
            hint={
              !invoiceId
                ? 'Elegí una factura de la marca si el ajuste tiene que salir también en ARCA.'
                : sign === '-'
                  ? 'Sale una nota de crédito de la marca sobre esa factura (no puede superarla).'
                  : 'Sale una nota de débito de la marca sobre esa factura.'
            }
          >
            <Select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}>
              <option value="">Sin comprobante (solo en la cuenta)</option>
              {bills.map((b) => (
                <option key={b.id} value={b.id}>
                  {fiscalLabel(b)} · {b.concept} · {formatMoney(b.totalCents)}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={busy} onClick={() => void save()}>
            Guardar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- regalías
function lastPeriods(n: number): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let k = 0; k < n; k++) {
    const x = new Date(d.getFullYear(), d.getMonth() - k, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

function Royalties() {
  const qc = useQueryClient();
  const toast = useToast();
  const periods = lastPeriods(12);
  const [period, setPeriod] = useState(periods[1]!);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['franchises', 'royalties', period],
    queryFn: () => api.get<FranchiseRoyaltyDTO[]>(`/franchises/royalties?period=${period}`),
  });
  const closed = period !== periods[0];
  const pending = q.data?.filter((r) => !r.charged && r.royaltyCents > 0) ?? [];

  async function charge() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<FranchiseRoyaltyDTO[]>('/franchises/royalties/charge', { period });
      qc.setQueryData(['franchises', 'royalties', period], r);
      await qc.invalidateQueries({ queryKey: ['franchises'] });
      toast.success('Regalías cargadas', `${periodLabel(period)} · en la cuenta corriente de cada franquicia`);
      for (const x of r) warnInvoice(toast, x.invoiceProblem, `Regalías de ${x.branchName}`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Regalías"
        subtitle="El porcentaje de cada franquicia sobre lo que vendió en el mes. Cuando el mes termina, se cargan a su cuenta corriente (una sola vez)."
        actions={
          <Select aria-label="Mes" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {periods.map((p, n) => (
              <option key={p} value={p}>
                {periodLabel(p)}
                {n === 0 ? ' (en curso)' : ''}
              </option>
            ))}
          </Select>
        }
      />
      <CardBody flush>
        {!q.data ? (
          <Skeleton style={{ height: 120, margin: 16 }} />
        ) : (
          <div className="bf-table-wrap">
            <table className="bf-table">
              <thead>
                <tr>
                  <th>Franquicia</th>
                  <th className="bf-num">Ventas del mes</th>
                  <th className="bf-num">%</th>
                  <th className="bf-num">Regalías</th>
                  <th className="bf-num">IVA</th>
                  <th className="bf-num">Total</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((r) => (
                  <tr key={r.branchId}>
                    <td className="bf-cell-main">{r.branchName}</td>
                    <td className="bf-num">{formatMoney(r.salesCents)}</td>
                    <td className="bf-num">{r.royaltyPct} %</td>
                    <td className="bf-num">{formatMoney(r.royaltyCents)}</td>
                    <td className="bf-num">{r.vatCents ? formatMoney(r.vatCents) : '—'}</td>
                    <td className="bf-num">{formatMoney(r.totalCents)}</td>
                    <td>
                      {r.charged ? (
                        <>
                          <Badge tone="success">Cargadas</Badge>
                          {r.fiscal && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                              <span className="bf-cell-sub">{fiscalLabel(r.fiscal)}</span>
                              <FiscalStatus f={r.fiscal} />
                              <PrintInvoiceButton base="/franchises/invoices" id={r.fiscal.id} />
                            </div>
                          )}
                        </>
                      ) : r.royaltyCents > 0 ? (
                        <Badge tone="warning">{closed ? 'Sin cargar' : 'Mes en curso'}</Badge>
                      ) : (
                        <span className="bf-cell-sub">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
      <CardBody>
        {error && <Callout tone="danger">{error}</Callout>}
        <Button variant="primary" icon={<HandCoins />} loading={busy} disabled={!closed || !pending.length} onClick={() => void charge()}>
          Cargar regalías de {periodLabel(period)} a las cuentas
        </Button>
        {!closed && <p className="bf-hint">Las regalías se cargan cuando termina el mes.</p>}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------- facturas
function Invoices({ list }: { list: FranchiseOverviewDTO[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const arca = useArca();
  const [branchId, setBranchId] = useState('');
  const range = useListRange();
  const q = useQuery({
    queryKey: ['franchises', 'invoices', branchId || 'all', ...range.key],
    queryFn: () => api.get<Paged<FranchiseInvoiceDTO>>(`/franchises/invoices?${branchId ? `branchId=${branchId}&` : ''}${range.qs}`),
    enabled: arca,
    placeholderData: keepPreviousData,
  });
  if (!arca)
    return (
      <EmptyState
        icon={<FileText />}
        title="Facturación electrónica no activada"
        text="Para que la marca les facture a sus franquicias por ARCA, activá la facturación electrónica en Configuración → Facturación."
      />
    );
  return (
    <Card>
      <CardHeader
        title="Facturas de la marca a sus franquicias"
        subtitle="Las emite la marca con su CUIT y su certificado de ARCA, al CUIT de cada franquicia: mercadería vendida, regalías y notas de crédito o débito."
        actions={
          <Select
            aria-label="Franquicia"
            value={branchId}
            onChange={(e) => {
              setBranchId(e.target.value);
              range.setPage(1);
            }}
          >
            <option value="">Todas las franquicias</option>
            {list.map((f) => (
              <option key={f.branchId} value={f.branchId}>
                {f.name}
              </option>
            ))}
          </Select>
        }
      />
      <CardBody flush>
        {!q.data ? (
          <Skeleton style={{ height: 120, margin: 16 }} />
        ) : (
          <>
            <div style={{ padding: '0 16px 12px' }}>
              <RangeFilter range={range} />
            </div>
            <InvoicesTable
              invoices={q.data.items}
              base="/franchises/invoices"
              showBranch={!branchId}
              onRetry={async (i) => {
                try {
                  const r = await api.post<{ status: string; error: string | null }>(`/franchises/invoices/${i.id}/retry`);
                  if (r.status === 'AUTHORIZED') toast.success('CAE obtenido', fiscalLabel(i));
                  else toast.error('Todavía sin CAE', r.error ?? 'ARCA no respondió. Se reintenta sola.');
                } catch (err) {
                  toast.error('No se pudo reintentar', errorMessage(err));
                }
                await qc.invalidateQueries({ queryKey: ['franchises'] });
              }}
            />
            <Pager range={range} total={q.data.total} />
          </>
        )}
      </CardBody>
      <CardBody>
        <p className="bf-hint" style={{ margin: 0 }}>
          Las facturas pendientes se reintentan solas cada pocos minutos. Si a una franquicia le faltan los datos fiscales o a la marca su
          certificado, el movimiento queda en la cuenta corriente y la factura se emite ahí con “Facturar”.
        </p>
      </CardBody>
    </Card>
  );
}
